import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

/**
 * Discord Connection Reminder Component
 * 
 * Shows a subtle reminder for users who haven't connected Discord yet
 * Appears after a delay and can be dismissed temporarily
 */
const DiscordConnectionReminder = ({ onConnectClick }) => {
  const { profile } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if user should see the reminder
  useEffect(() => {
    // Don't show if Discord is already connected
    if (profile?.discord_username) {
      setIsVisible(false);
      return;
    }

    // Don't show if dismissed in this session
    if (isDismissed) {
      setIsVisible(false);
      return;
    }

    // Check if user has dismissed it recently (localStorage)
    const dismissedUntil = localStorage.getItem('discord-reminder-dismissed');
    if (dismissedUntil && new Date().getTime() < parseInt(dismissedUntil)) {
      setIsVisible(false);
      return;
    }

    // Show reminder after a delay (5 seconds)
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [profile?.discord_username, isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    
    // Remember dismissal for 24 hours
    const dismissUntil = new Date().getTime() + (24 * 60 * 60 * 1000);
    localStorage.setItem('discord-reminder-dismissed', dismissUntil.toString());
  };

  const handleConnect = () => {
    setIsVisible(false);
    if (onConnectClick) {
      onConnectClick();
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.9 }}
        transition={{ duration: 0.3, type: "spring", stiffness: 300, damping: 30 }}
        className="fixed bottom-20 right-4 z-40 md:bottom-6 md:right-6"
      >
        <div className="bg-gradient-to-r from-[#7289DA]/10 to-[#7289DA]/5 backdrop-blur-xl border border-[#7289DA]/20 rounded-xl p-4 max-w-sm shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-[#7289DA]/20 rounded-lg flex items-center justify-center">
                <Icon name="MessageCircle" size={18} className="text-[#7289DA]" />
              </div>
              <h4 className="text-white font-semibold text-sm">Discord Integration</h4>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/60 hover:text-white/80 transition-colors p-1"
            >
              <Icon name="X" size={14} />
            </button>
          </div>

          {/* Content */}
          <div className="mb-4">
            <p className="text-white/80 text-sm mb-2">
              💬 Get instant notifications via Discord DMs when you receive new messages!
            </p>
            <ul className="text-white/60 text-xs space-y-1">
              <li>• Instant message notifications</li>
              <li>• Reply directly in Discord</li>
              <li>• Never miss important updates</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex space-x-2">
            <button
              onClick={handleConnect}
              className="flex-1 bg-[#7289DA] hover:bg-[#677BC4] text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Icon name="Link" size={14} />
              <span>Connect Discord</span>
            </button>
            <button
              onClick={handleDismiss}
              className="text-white/60 hover:text-white/80 text-xs px-2 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DiscordConnectionReminder;